import { useCallback } from 'react';
import { useFocusEffect, usePathname } from 'expo-router';

import { useNavigationReset } from '../../../../routing';

// ─────────────────────────────────────────────────────────────────────────────

// Here we handle 301 redirects using the redirects mapping.
export const usePermanentRedirects = () => {
  const pathname = usePathname();
  const resetNavigation = useNavigationReset();

  const redirectTo = pathname ? REDIRECTS[pathname] : null;

  useFocusEffect(
    useCallback(() => {
      if (redirectTo) {
        resetNavigation(getResetState(redirectTo));
      }
    }, [redirectTo, resetNavigation]),
  );

  return Boolean(redirectTo);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getResetState(mapping: TabRouteMapping): ResetState {
  const { tabName, screenName } = mapping;

  if (!screenName) {
    return {
      index: 0,
      routes: [{ name: '(tabs)', state: { routes: [{ name: tabName }] } }],
    };
  }

  return {
    index: 0,
    routes: [
      {
        name: '(tabs)',
        state: { routes: [{ name: tabName, state: { routes: [{ name: screenName }] } }] },
      },
    ],
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const REDIRECTS: Record<string, TabRouteMapping> = {
  '/location': { tabName: '(menu)', screenName: 'locations/index' },
  '/menu': { tabName: 'account', screenName: 'menu/index' },
  '/profile': { tabName: 'account', screenName: 'info/index' },
  '/account/profile': { tabName: 'account', screenName: 'info/index' },
  '/challenges': { tabName: 'rewards' },
  '/sg-rewards': { tabName: 'rewards' },
  '/payment': { tabName: 'account', screenName: 'payments/index' },
  '/account/payment-and-gift-cards': { tabName: 'account', screenName: 'payments/index' },
  '/addresses': { tabName: 'account', screenName: 'addresses/index' },
  '/orders': { tabName: 'account', screenName: 'orders/index' },
  '/favorites': { tabName: 'account', screenName: 'favorites/index' },
  '/account-dietary-restrictions': { tabName: 'account', screenName: 'dietary-restrictions/index' },
};

// ─── Types ───────────────────────────────────────────────────────────────────

type TabRouteMapping = {
  tabName: string;
  screenName?: string;
};

type ResetState = Parameters<ReturnType<typeof useNavigationReset>>[0];
